// src/components/MinecraftStorePage.jsx
import { useState, useEffect } from "react";
import CheckoutModal from "./CheckoutModal";
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Check, Package } from "lucide-react";
import { useCurrency, fmtPrice } from "../hooks/useCurrency";

const DISCORD_URL = "https://discord.gg/AzrauJnuqw";

/* ─── Products ────────────────────────────────────────────────────────────── */
const RANKS = [
  {
    id: "vip", type: "rank",
    name: "VIP", subtitle: "Membership",
    price: 4.99, period: "/ month",
    color: "#c9a84c", dimColor: "rgba(201,168,76,.12)",
    borderColor: "rgba(201,168,76,.4)",
    glowColor: "rgba(201,168,76,.25)",
    cardBg: "linear-gradient(160deg,#1c1500,#2e2000,#1c1500)",
    icon: "/vip-icon.png",
    perks: [
      { label: "3 free /sethome slots" },
      { label: "VIP Particle Store access" },
      { label: "VIP Custom Items Store access" },
      { label: "VIP Furniture Store access" },
      { label: "Exclusive [VIP] chat prefix" },
      { label: "VIP Discord role" },
    ],
  },
  {
    id: "donator", type: "rank",
    name: "Donator", subtitle: "Supporter",
    price: 10.00, period: "one-time",
    color: "#a78bfa", dimColor: "rgba(167,139,250,.12)",
    borderColor: "rgba(167,139,250,.4)",
    glowColor: "rgba(167,139,250,.22)",
    cardBg: "linear-gradient(160deg,#0f0a1e,#1a1035,#0f0a1e)",
    icon: "/vip-icon.png",
    perks: [
      { label: "Exclusive [Donator] chat prefix" },
      { label: "Donator Discord role" },
      { label: "Donator-only giveaway access" },
      { label: "Support the server ❤" },
    ],
  },
];

const COINS = [
  {
    id: "coins-200", type: "coins",
    name: "200", subtitle: "TOA Coins",
    price: 4.99,
    bonus: null, popular: false,
    color: "#a78bfa",
    dimColor: "rgba(167,139,250,.12)",
    borderColor: "rgba(167,139,250,.35)",
    glowColor: "rgba(167,139,250,.2)",
    cardBg: "linear-gradient(160deg,#0f0a1e,#180f2e,#0f0a1e)",
  },
  {
    id: "coins-500", type: "coins",
    name: "500", subtitle: "TOA Coins",
    price: 10.49,
    bonus: "+50 FREE", popular: true,
    color: "#c9a84c",
    dimColor: "rgba(201,168,76,.12)",
    borderColor: "rgba(201,168,76,.4)",
    glowColor: "rgba(201,168,76,.22)",
    cardBg: "linear-gradient(160deg,#1c1500,#2e2000,#1c1500)",
  },
  {
    id: "coins-1200", type: "coins",
    name: "1200", subtitle: "TOA Coins",
    price: 23.99,
    bonus: "+200 FREE", popular: false,
    color: "#4ade80",
    dimColor: "rgba(74,222,128,.1)",
    borderColor: "rgba(74,222,128,.35)",
    glowColor: "rgba(74,222,128,.18)",
    cardBg: "linear-gradient(160deg,#061209,#0c2010,#061209)",
  },
];

/* ─── Pixel decorative divider ─────────────────────────────────────────────  */
function McRule({ color = "#7b5ea7", opacity = 0.5 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, width: "100%", height: 8, margin: "0 0 4px" }}>
      {Array.from({ length: 40 }).map((_, i) => (
        <div key={i} style={{
          flex: 1, height: i % 4 === 0 ? 8 : i % 2 === 0 ? 5 : 3,
          background: color,
          opacity: opacity * (0.4 + (i % 3) * 0.3),
          marginRight: 1,
        }} />
      ))}
    </div>
  );
}

const stonePattern = {
  backgroundImage: [
    "repeating-linear-gradient(0deg, transparent, transparent 15px, rgba(0,0,0,.06) 15px, rgba(0,0,0,.06) 16px)",
    "repeating-linear-gradient(90deg, transparent, transparent 15px, rgba(0,0,0,.06) 15px, rgba(0,0,0,.06) 16px)",
  ].join(","),
};

/* ─── Cart Drawer ──────────────────────────────────────────────────────────  */
function CartDrawer({ cart, onClose, onRemove, onQtyChange, onCheckout, symbol }) {
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", backdropFilter: "blur(6px)", zIndex: 600, animation: "toa-fadein .15s ease" }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(380px,100vw)",
        background: "#0d0b18", borderLeft: "3px solid #2a2040",
        zIndex: 601, display: "flex", flexDirection: "column",
        boxShadow: "-24px 0 80px rgba(0,0,0,.8)",
        animation: "cart-slide .2s cubic-bezier(.22,.68,0,1.2)",
      }}>
        <style>{`
          @keyframes cart-slide { from { transform: translateX(110%) } to { transform: translateX(0) } }
          @keyframes enchant-glow { 0%,100% { box-shadow: 0 0 12px rgba(123,94,167,.4) } 50% { box-shadow: 0 0 28px rgba(123,94,167,.8), 0 0 50px rgba(123,94,167,.3) } }
        `}</style>
        <McRule color="#7b5ea7" opacity={0.6} />
        <div style={{ padding: "16px 20px", background: "linear-gradient(135deg,#130f22,#1a1530)", borderBottom: "2px solid #2a2040", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: "rgba(123,94,167,.2)", border: "2px solid rgba(123,94,167,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingCart style={{ width: 15, height: 15, color: "#9b7fd4" }} />
            </div>
            <span style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#e2d9f3", letterSpacing: 2 }}>CART</span>
            {cart.length > 0 && (
              <span style={{ background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)", color: "#fff", width: 22, height: 22, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontFamily: "'Courier New',monospace", border: "1px solid rgba(123,94,167,.6)" }}>
                {cart.reduce((s, i) => s + i.qty, 0)}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.04)", border: "2px solid #2a2040", width: 30, height: 30, cursor: "pointer", color: "#6b6080", display: "flex", alignItems: "center", justifyContent: "center", transition: ".15s" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "#7b5ea7"; e.currentTarget.style.color = "#fff"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#6b6080"; }}>
            <X style={{ width: 12, height: 12 }} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px 20px" }}>
              <div style={{ width: 56, height: 56, background: "rgba(123,94,167,.08)", border: "2px solid #2a2040", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ShoppingCart style={{ width: 24, height: 24, color: "#3d3060", opacity: .5 }} />
              </div>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "#4a4060", letterSpacing: 2 }}>CART IS EMPTY</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {cart.map(item => (
                <div key={item.id} style={{ background: "linear-gradient(135deg,#110e1e,#1a1530)", border: `2px solid ${item.borderColor}`, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 40, height: 40, flexShrink: 0, background: item.dimColor, border: `2px solid ${item.borderColor}`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {item.type === "coins"
                      ? <img src="/toa_coin_32.png" alt="coin" style={{ width: 28, height: 28, imageRendering: "pixelated" }} />
                      : <img src="/vip-icon.png" alt="rank" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: item.color, fontWeight: 700, letterSpacing: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.name} {item.subtitle}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b6080", marginTop: 2, fontFamily: "'Courier New',monospace" }}>
                      {fmtPrice(item.price, symbol)}{item.period ? ` ${item.period}` : ""}
                    </div>
                  </div>
                  {item.type === "coins" ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <button onClick={() => onQtyChange(item.id, item.qty - 1)} style={{ width: 22, height: 22, background: "rgba(255,255,255,.05)", border: "1px solid #2a2040", color: "#9b7fd4", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Minus style={{ width: 9, height: 9 }} />
                      </button>
                      <span style={{ fontSize: 12, color: "#e2d9f3", width: 16, textAlign: "center", fontFamily: "'Courier New',monospace" }}>{item.qty}</span>
                      <button onClick={() => onQtyChange(item.id, item.qty + 1)} style={{ width: 22, height: 22, background: "rgba(255,255,255,.05)", border: "1px solid #2a2040", color: "#9b7fd4", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Plus style={{ width: 9, height: 9 }} />
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontSize: 10, color: "#4a4060", fontFamily: "'Cinzel',serif" }}>×1</span>
                  )}
                  <button onClick={() => onRemove(item.id)} style={{ background: "none", border: "none", color: "#4a3050", cursor: "pointer", padding: 4, transition: ".15s" }}
                    onMouseOver={e => e.currentTarget.style.color = "#ef4444"}
                    onMouseOut={e => e.currentTarget.style.color = "#4a3050"}>
                    <Trash2 style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div>
            <McRule color="#7b5ea7" opacity={0.4} />
            <div style={{ padding: "16px 20px", background: "linear-gradient(135deg,#130f22,#1a1530)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "#8b7fa0", letterSpacing: 2 }}>TOTAL</span>
                <span style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 22, color: "#c9a84c" }}>{fmtPrice(total, symbol)}</span>
              </div>
              <button onClick={onCheckout} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg,#5a2d8a,#7b3faa,#5a2d8a)", border: "2px solid rgba(123,94,167,.6)", color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: ".2s", animation: "enchant-glow 2s ease-in-out infinite" }}
                onMouseOver={e => e.currentTarget.style.background = "linear-gradient(135deg,#7b3faa,#9a50cc,#7b3faa)"}
                onMouseOut={e => e.currentTarget.style.background = "linear-gradient(135deg,#5a2d8a,#7b3faa,#5a2d8a)"}>
                Proceed to Checkout
              </button>
              <div style={{ fontSize: 10, color: "#8070a8", textAlign: "center", marginTop: 10, fontFamily: "'Cinzel',serif", letterSpacing: 1 }}>
                PURCHASES ARE FINAL · SUPPORT VIA DISCORD
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Add-to-cart button with inline animation ─────────────────────────────
   - First click: button flashes green "✓ Added to Cart"
   - Subsequent clicks (coins only): shows "✓ In Cart ×2", ×3, etc.
   - Ranks are limited to qty 1
──────────────────────────────────────────────────────────────────────────── */
function CartButton({ product, cart, onAdd, color, borderColor }) {
  const [flash, setFlash] = useState(false);
  const item = cart.find(i => i.id === product.id);
  const qty = item ? item.qty : 0;

  const handleClick = () => {
    if (product.type === "rank" && qty >= 1) return;
    onAdd(product);
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
  };

  const isAdded = qty > 0;
  const bg = flash
    ? "linear-gradient(135deg,rgba(74,222,128,.3),rgba(74,222,128,.15))"
    : isAdded
      ? "linear-gradient(135deg,rgba(74,222,128,.15),rgba(74,222,128,.08))"
      : `linear-gradient(135deg,${color}22,${color}11)`;
  const border = flash ? "rgba(74,222,128,.7)" : isAdded ? "rgba(74,222,128,.45)" : borderColor;
  const textColor = flash || isAdded ? "#4ade80" : color;

  return (
    <button
      className="mc-add-btn"
      onClick={handleClick}
      disabled={product.type === "rank" && qty >= 1 && !flash}
      style={{
        width: "100%", padding: "10px",
        background: bg,
        border: `2px solid ${border}`,
        color: textColor,
        fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700,
        letterSpacing: 2, textTransform: "uppercase",
        cursor: product.type === "rank" && qty >= 1 ? "default" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        transition: "background .2s, border-color .2s, color .2s",
        position: "relative", zIndex: 1,
      }}
    >
      {flash ? (
        <><Check style={{ width: 12, height: 12 }} /> Added to Cart{qty > 0 ? ` ×${qty}` : ""}</>
      ) : isAdded ? (
        <><Check style={{ width: 12, height: 12 }} /> In Cart{qty > 1 ? ` ×${qty}` : ""}</>
      ) : (
        <><ShoppingCart style={{ width: 12, height: 12 }} /> Add to Cart</>
      )}
    </button>
  );
}

/* ─── Main Store Page ──────────────────────────────────────────────────────  */
export default function MinecraftStorePage({ onClose, initialTab = "ranks", onOpenAuth }) {
  const { symbol } = useCurrency();
  const [tab, setTab] = useState(initialTab);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  // Auto-open checkout modal if Stripe redirected back after a redirect-based payment
  const stripeParams = new URLSearchParams(window.location.search);
  const hasStripeRedirect = !!stripeParams.get('redirect_status');
  const [checkoutOpen, setCheckoutOpen] = useState(hasStripeRedirect);

  // Clean up Stripe URL params so they don't persist on refresh
  useEffect(() => {
    if (!hasStripeRedirect) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('redirect_status');
    window.history.replaceState({}, '', url.toString());
  }, []); // eslint-disable-line

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const addToCart = product => {
    setCart(prev => {
      const ex = prev.find(i => i.id === product.id);
      if (ex) {
        if (product.type === "rank") return prev;
        return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  const removeFromCart = id => setCart(prev => prev.filter(i => i.id !== id));
  const changeQty = (id, qty) => qty <= 0 ? removeFromCart(id) : setCart(prev => prev.map(i => i.id === id ? { ...i, qty } : i));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, overflowY: "auto", overflowX: "hidden", background: "#08060f" }}>
      {/* Mobile-safe background — rendered as sticky behind content */}
      <div aria-hidden="true" style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "url('/mc-background.png')", backgroundSize: "cover", backgroundPosition: "center center", backgroundAttachment: "scroll", filter: "brightness(0.22) saturate(0.8)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(170deg,rgba(8,6,15,.85) 0%,rgba(10,8,20,.7) 40%,rgba(6,10,8,.82) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, ...stonePattern, opacity: 0.25 }} />
        <div style={{ position: "absolute", top: "-5%", left: "50%", transform: "translateX(-50%)", width: "70%", height: "45%", background: "radial-gradient(ellipse,rgba(123,94,167,.1) 0%,transparent 65%)" }} />
        <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "60%", height: "25%", background: "radial-gradient(ellipse,rgba(201,168,76,.06) 0%,transparent 70%)" }} />
        <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: "linear-gradient(90deg,transparent,rgba(123,94,167,.06),transparent)", animation: "scanline 8s linear infinite" }} />
      </div>
      <style>{`
        @keyframes logo-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
        @keyframes coin-bob { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-7px) rotate(3deg)} }
        @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
        @keyframes enchant-glow { 0%,100%{box-shadow:0 0 12px rgba(123,94,167,.3)} 50%{box-shadow:0 0 28px rgba(123,94,167,.7),0 0 50px rgba(123,94,167,.2)} }
        .mc-card-hover { transition: transform .22s, box-shadow .22s; }
        .mc-card-hover:hover { transform: translateY(-5px) !important; }
        .mc-tab-btn { transition: background .18s, color .18s, border-color .18s; }
        .mc-add-btn { transition: background .2s, border-color .2s, color .2s; }
        .mc-add-btn:hover:not(:disabled) { filter: brightness(1.15); }
      `}</style>



      {/* Top bar — Minecraft dirt/stone block pattern */}
      <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
        {/* Minecraft block row — matches cart sidebar McRule palette */}
        <div style={{ display: "flex", height: 8, overflow: "hidden" }}>
          {Array.from({ length: 48 }).map((_, i) => {
            // Same palette as CartDrawer McRule (#7b5ea7) and its background tones
            const deep    = ["#0d0b18","#110e1e","#130f22","#1a1530","#2a2040"];
            const mid     = ["#2a2040","#3a2d54","#2a1f42","#4a3570","#3a2d54"];
            const purple  = ["#5a3d8a","#7b5ea7","#6a4e98","#9b7fd4","#7b5ea7"];
            const palette = i % 5 === 0 ? purple : i % 3 === 0 ? mid : deep;
            return <div key={i} style={{ flex: 1, background: palette[i % palette.length], borderRight: "1px solid rgba(0,0,0,.45)" }} />;
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: 52, background: "rgba(8,6,15,.92)", backdropFilter: "blur(20px)", borderBottom: "2px solid #1a1530", position: "relative", overflow: "hidden" }}>
          {/* Stone block texture inside bar */}
          <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(90deg,rgba(255,255,255,.015) 0px,rgba(255,255,255,.015) 1px,transparent 1px,transparent 18px),repeating-linear-gradient(0deg,rgba(255,255,255,.015) 0px,rgba(255,255,255,.015) 1px,transparent 1px,transparent 18px)", pointerEvents: "none" }} />
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,.04)", border: "2px solid #1e1a30", color: "#8b7fa0", fontSize: 11, fontFamily: "'Cinzel',serif", letterSpacing: 1.5, cursor: "pointer", padding: "7px 14px", transition: ".15s", textTransform: "uppercase" }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(123,94,167,.5)"; e.currentTarget.style.color = "#c9a84c"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = "#1e1a30"; e.currentTarget.style.color = "#8b7fa0"; }}>
          <ArrowLeft style={{ width: 12, height: 12 }} /> Back
        </button>
          <button onClick={() => setCartOpen(true)} style={{ display: "flex", alignItems: "center", gap: 8, background: cartCount > 0 ? "rgba(123,94,167,.18)" : "rgba(255,255,255,.04)", border: `2px solid ${cartCount > 0 ? "rgba(123,94,167,.5)" : "#1e1a30"}`, color: cartCount > 0 ? "#c9a84c" : "#8b7fa0", fontFamily: "'Cinzel',serif", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", padding: "7px 14px", transition: ".2s" }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "rgba(123,94,167,.6)"; e.currentTarget.style.color = "#c9a84c"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = cartCount > 0 ? "rgba(123,94,167,.5)" : "#1e1a30"; e.currentTarget.style.color = cartCount > 0 ? "#c9a84c" : "#8b7fa0"; }}>
          <ShoppingCart style={{ width: 14, height: 14 }} />
          Cart
          {cartCount > 0 && (
            <span style={{ background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)", color: "#fff", width: 20, height: 20, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontFamily: "'Courier New',monospace" }}>
              {cartCount}
            </span>
          )}
        </button>
        </div>
      </div>

      {/* Page content */}
      <div style={{ position: "relative", zIndex: 1 }}>

        {/* Hero */}
        <div style={{ textAlign: "center", padding: "52px 24px 36px", position: "relative" }}>
          <div style={{ display: "inline-block", marginBottom: 28, animation: "logo-float 4s ease-in-out infinite", filter: "drop-shadow(0 0 24px rgba(123,94,167,.5)) drop-shadow(0 0 48px rgba(201,168,76,.2))" }}>
            <img src="/toa-mc-logo.png" alt="TOA" style={{ width: 200, height: 200, imageRendering: "pixelated", display: "block" }} />
          </div>
          <div style={{ fontSize: 10, letterSpacing: 6, fontFamily: "'Cinzel',serif", color: "rgba(201,168,76,.82)", textTransform: "uppercase", marginBottom: 14 }}>
            Rise Above · Grow Together
          </div>
          <p style={{ fontSize: 14, color: "#a090c0", maxWidth: 360, margin: "0 auto 28px", lineHeight: 1.75, fontFamily: "'Cinzel',serif", letterSpacing: .5 }}>
            Unlock ranks &amp; TOA Coins on the SMP
          </p>
          <div style={{ maxWidth: 500, margin: "0 auto" }}>
            <McRule color="#c9a84c" opacity={0.35} />
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 40px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "2px solid #1e1a30", maxWidth: 380, margin: "0 auto 48px", overflow: "hidden" }}>
            {[
              { id: "ranks", label: "Ranks", icon: <img src="/vip-icon.png" alt="rank" style={{ width: 20, height: 20, imageRendering: "pixelated", objectFit: "contain" }} /> },
              { id: "coins", label: "TOA Coins", icon: <img src="/toa_coin_32.png" alt="coin" style={{ width: 18, height: 18, imageRendering: "pixelated" }} /> },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className="mc-tab-btn" style={{
                padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
                fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
                border: "none", cursor: "pointer",
                background: tab === t.id ? "linear-gradient(135deg,rgba(123,94,167,.35),rgba(90,61,138,.2))" : "rgba(255,255,255,.02)",
                color: tab === t.id ? "#e2d9f3" : "#4a4060",
                borderBottom: tab === t.id ? "3px solid #9b7fd4" : "3px solid transparent",
                boxShadow: tab === t.id ? "inset 0 -1px 0 rgba(123,94,167,.3)" : "none",
              }}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          {/* RANKS */}
          {tab === "ranks" && (
            <div style={{ paddingBottom: 80 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,360px))", gap: 20, justifyContent: "center" }}>
                {RANKS.map(rank => (
                  <div key={rank.id} className="mc-card-hover" style={{ background: rank.cardBg, border: `2px solid ${rank.borderColor}`, position: "relative", overflow: "hidden", boxShadow: `0 8px 40px ${rank.glowColor}`, display: "flex", flexDirection: "column" }}>
                    <div style={{ position: "absolute", inset: 0, ...stonePattern, opacity: 0.3, pointerEvents: "none" }} />

                    {/* Compact header row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px 10px", borderBottom: `1px solid ${rank.borderColor}`, position: "relative", zIndex: 1 }}>
                      <img src={rank.icon} alt={rank.name} style={{ width: 42, height: 42, imageRendering: "pixelated", objectFit: "contain", flexShrink: 0, filter: `drop-shadow(0 0 8px ${rank.glowColor})` }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 8, letterSpacing: 4, fontFamily: "'Cinzel',serif", color: `${rank.color}99`, textTransform: "uppercase", marginBottom: 2 }}>Server Rank</div>
                        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 18, color: rank.color, lineHeight: 1, textShadow: `0 0 14px ${rank.glowColor}` }}>{rank.name}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 18, color: "#e2d9f3" }}>{fmtPrice(rank.price, symbol)}</div>
                        <div style={{ fontSize: 10, color: "#6b7080", fontFamily: "'Cinzel',serif" }}>{rank.period}</div>
                      </div>
                    </div>

                    {/* Perks */}
                    <div style={{ padding: "10px 14px 12px", position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 8px", marginBottom: 10 }}>
                        {rank.perks.map(perk => (
                          <div key={perk.label} style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                            <Check style={{ width: 9, height: 9, color: rank.color, flexShrink: 0, marginTop: 3 }} />
                            <div style={{ fontSize: 10, color: "#c0b8d0", lineHeight: 1.35, fontFamily: "'Cinzel',serif" }}>{perk.label}</div>
                          </div>
                        ))}
                      </div>
                      {rank.id !== "donator" && (
                        <div style={{ background: "rgba(123,94,167,.1)", border: "1px solid rgba(123,94,167,.25)", padding: "6px 10px", marginBottom: 10, fontSize: 9, color: "#9a88c0", lineHeight: 1.5, fontFamily: "'Cinzel',serif", letterSpacing: .2 }}>
                          ⚠ Store access unlocks purchasing ability — items not included free.
                        </div>
                      )}
                      <div style={{ marginTop: "auto" }}>
                        <CartButton product={rank} cart={cart} onAdd={addToCart} color={rank.color} borderColor={rank.borderColor} />
                      </div>
                    </div>

                    <McRule color={rank.color} opacity={0.3} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* COINS */}
          {tab === "coins" && (
            <div style={{ paddingBottom: 80 }}>
              <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div style={{ fontSize: 10, letterSpacing: 4, fontFamily: "'Cinzel',serif", color: "rgba(155,127,212,.95)", textTransform: "uppercase" }}>
                  In-Game Currency
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 18 }}>
                {COINS.map((pkg, idx) => (
                  <div key={pkg.id} className="mc-card-hover" style={{ background: pkg.cardBg, border: `2px solid ${pkg.borderColor}`, padding: "26px 22px", textAlign: "center", position: "relative", overflow: "hidden", boxShadow: `0 8px 30px ${pkg.glowColor}`, "--coin-delay": idx === 0 ? "0s" : idx === 1 ? "0.6s" : "1.2s" }}>
                    <div style={{ position: "absolute", inset: 0, ...stonePattern, opacity: 0.25, pointerEvents: "none" }} />
                    <McRule color={pkg.color} opacity={0.35} />
                    <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 0%,${pkg.dimColor},transparent 60%)`, pointerEvents: "none" }} />

                    {pkg.popular && (
                      <div style={{ position: "absolute", top: 18, right: 14, background: "linear-gradient(135deg,#c9a84c,#8a6020)", color: "#0d0800", fontSize: 9, fontFamily: "'Cinzel',serif", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", padding: "3px 10px" }}>
                        Best Value
                      </div>
                    )}

                    <div style={{ position: "relative", zIndex: 1, padding: "16px 0 12px" }}>
                      <img src="/toa-coin.png" alt="coin" style={{ width: 72, height: 72, objectFit: "contain", imageRendering: "pixelated", filter: `drop-shadow(0 4px 14px ${pkg.glowColor})`, display: "block", margin: "0 auto", animation: "coin-bob 2.8s ease-in-out infinite", animationDelay: "var(--coin-delay, 0s)" }} />
                    </div>

                    <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 30, color: pkg.color, position: "relative", zIndex: 1, marginBottom: 2 }}>
                      {parseInt(pkg.name).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 9, color: "#7a6aaa", letterSpacing: 4, fontFamily: "'Cinzel',serif", textTransform: "uppercase", position: "relative", zIndex: 1, marginBottom: 12 }}>
                      TOA Coins
                    </div>

                    {pkg.bonus ? (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontFamily: "'Cinzel',serif", color: "#4ade80", marginBottom: 14, background: "rgba(74,222,128,.08)", border: "1px solid rgba(74,222,128,.25)", padding: "4px 12px", position: "relative", zIndex: 1, letterSpacing: 1 }}>
                        <Package style={{ width: 9, height: 9 }} /> {pkg.bonus}
                      </div>
                    ) : (
                      <div style={{ height: 30, position: "relative", zIndex: 1 }} />
                    )}

                    <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 22, color: "#e2d9f3", position: "relative", zIndex: 1, marginBottom: 18 }}>
                      {fmtPrice(pkg.price, symbol)}
                    </div>

                    <CartButton product={pkg} cart={cart} onAdd={addToCart} color={pkg.color} borderColor={pkg.borderColor} />

                    <McRule color={pkg.color} opacity={0.25} />
                  </div>
                ))}
              </div>

              <div style={{ textAlign: "center", marginTop: 36, fontSize: 11, color: "#7a6aaa", lineHeight: 1.8, fontFamily: "'Cinzel',serif", letterSpacing: .5 }}>
                TOA Coins are non-refundable · Valid on{" "}
                <span style={{ color: "#9b7fd4" }}>play.theonesabove.org</span> only
                <br />
                <a href={DISCORD_URL} target="_blank" rel="noreferrer" style={{ color: "#7b5ea7", textDecoration: "none" }}>Contact support on Discord</a>
              </div>
            </div>
          )}
        </div>
      </div>

      {cartOpen && <CartDrawer cart={cart} onClose={() => setCartOpen(false)} onRemove={removeFromCart} onQtyChange={changeQty} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }} symbol={symbol} />}
      {checkoutOpen && <CheckoutModal cart={cart} onClose={() => setCheckoutOpen(false)} onOpenAuth={onOpenAuth} />}
    </div>
  );
}